import React, { useState, useEffect, useContext } from 'react';
import ApiURL from '../Api/Api';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import {
  PlusIcon,
  TrashIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/* ─────────────────────────────────────────────
   TOAST (fallback, but using react-toastify)
───────────────────────────────────────────── */
const CustomToast = ({ message, type }) => {
  if (!message) return null;
  return null; // Using react-toastify instead
};

/* ═══════════════════════════════════════════════════════════════════════ */
const Addattribute = () => {
  const companyId = localStorage.getItem('company_id') || '';
  const userId = localStorage.getItem('id') || '';
  const role = localStorage.getItem('role') || '';
  const currentUserName = localStorage.getItem('user_name') || 'admin';
  const { isSidebarOpen } = useContext(SidebarContext);
  const navigate = useNavigate();

  const newAttributeTemplate = () => ({
    created_by: currentUserName,
    created_date: new Date().toISOString().split('T')[0],
    main_head_name: '',
    sub_main_name: '',
    start_date: '',
    end_date: '',
  });

  const [inputSets, setInputSets] = useState([newAttributeTemplate()]);
  const [existingAttributes, setExistingAttributes] = useState([]);
  const [fetchPickers, setFetchPickers] = useState({});
  const [fieldSettings, setFieldSettings] = useState({
    travel_fields_enabled: true,
    travel_fields_required: false,
    remarks_required_for_other: true,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const mainHeadOptions = [...new Set(existingAttributes.map((attr) => attr.main_head_name).filter(Boolean))].sort();
  const subHeadOptionsFor = (mainHead) => [
    ...new Set(
      existingAttributes
        .filter((attr) => attr.main_head_name === mainHead)
        .map((attr) => attr.sub_main_name)
        .filter(Boolean)
    ),
  ].sort();

  useEffect(() => {
    const now = new Date();
    const formattedDate = now.toISOString().split('T')[0];
    setInputSets(prev => {
      const updated = [...prev];
      updated[0] = { ...updated[0], created_date: formattedDate };
      return updated;
    });
  }, []);

  useEffect(() => {
    const loadAttributes = async () => {
      try {
        const response = await fetch(`${ApiURL}/getAttributes.php?company_id=${encodeURIComponent(companyId)}`);
        const data = await response.json();
        if (data.status === true || data.status === 'success') {
          setExistingAttributes(Array.isArray(data.attributes) ? data.attributes : []);
          if (data.field_settings) setFieldSettings(data.field_settings);
        }
      } catch (error) {
        console.error('Error loading attributes:', error);
      }
    };
    loadAttributes();
  }, [companyId]);

  const handleAddInputSet = () => {
    setInputSets([...inputSets, newAttributeTemplate()]);
  };

  const handleRemoveInputSet = (index) => {
    if (inputSets.length === 1) return;
    setInputSets(prev => prev.filter((_, i) => i !== index));
  };

  const handleLinkClick = (route) => {
    navigate(route);
    setIsMenuOpen(false);
  };

  const goToDashboard = () => {
    handleLinkClick('/dashboard');
  };

  const handleInputChange = (index, field, value) => {
    const updatedInputSets = [...inputSets];
    updatedInputSets[index][field] = value;
    if (field === 'main_head_name') {
      updatedInputSets[index].sub_main_name = '';
    }
    setInputSets(updatedInputSets);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    const invalid = inputSets.some(set => 
      !set.main_head_name.trim() || 
      !set.sub_main_name.trim() ||
      !set.start_date || 
      !set.end_date
    );
    
    if (invalid) {
      toast.error('Please fill all required fields (Main Head, Sub Head, Start Date, End Date)');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetch(`${ApiURL}/Addattribute.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes: inputSets, company_id: companyId }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        toast.success(data.message || `${inputSets.length} attribute${inputSets.length > 1 ? 's' : ''} added successfully!`);
        setInputSets([newAttributeTemplate()]);
        const refresh = await fetch(`${ApiURL}/getAttributes.php?company_id=${encodeURIComponent(companyId)}`);
        const refreshedData = await refresh.json();
        if (refreshedData.status === true || refreshedData.status === 'success') {
          setExistingAttributes(Array.isArray(refreshedData.attributes) ? refreshedData.attributes : []);
        }
      } else {
        throw new Error(data.message || 'Failed to add attributes');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'An error occurred while submitting the form');
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveFieldSettings = async () => {
    try {
      const response = await fetch(`${ApiURL}/saveTransactionFieldSettings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fieldSettings, user_id: userId }),
      });
      const data = await response.json();
      if (data.status !== 'success') throw new Error(data.message || 'Failed to save settings');
      toast.success('Transaction field settings saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save settings');
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f6fa]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <main className={`flex-1 transition-all duration-300 w-full ${isSidebarOpen ? 'lg:ml-72' : ''}`}>
          <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-6 pb-16">

            {/* ── PAGE HEADER ── */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="bg-white rounded-2xl border border-slate-200 mb-5 shadow-sm overflow-hidden"
            >
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-5 py-5 sm:px-6 sm:py-6 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                    <p className="text-[9px] font-extrabold uppercase tracking-[0.25em] text-indigo-600">Attribute Management</p>
                  </div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight leading-none">Add Attributes</h1>
                  <p className="text-[11px] text-slate-400 mt-1.5 font-medium">
                    Create main heads and sub-heads for transaction categorization.
                  </p>
                </div>
                {/* Stats pill */}
                <div className="flex-shrink-0 bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-center">
                  <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-wider mb-0.5">Entries</p>
                  <p className="text-2xl font-black text-slate-800 leading-none">{inputSets.length}</p>
                </div>
              </div>
            </motion.div>

            {['company_owner', 'owner'].includes(role) && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.04, duration: 0.28 }}
                className="bg-white rounded-2xl border border-slate-200 mb-5 shadow-sm overflow-hidden"
              >
                <div className="h-[2px] bg-gradient-to-r from-emerald-500 to-indigo-500" />
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                      <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-0.5">Transaction Field Controls</p>
                      <h2 className="text-[13px] font-black text-slate-700">Travel and Remarks Rules</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 lg:max-w-3xl">
                      <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-600">
                        <input type="checkbox" checked={!!fieldSettings.travel_fields_enabled} onChange={(e) => setFieldSettings((prev) => ({ ...prev, travel_fields_enabled: e.target.checked }))} />
                        Show Travel Fields
                      </label>
                      <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-600">
                        <input type="checkbox" checked={!!fieldSettings.travel_fields_required} disabled={!fieldSettings.travel_fields_enabled} onChange={(e) => setFieldSettings((prev) => ({ ...prev, travel_fields_required: e.target.checked }))} />
                        Travel Fields Mandatory
                      </label>
                      <label className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold text-slate-600">
                        <input type="checkbox" checked={!!fieldSettings.remarks_required_for_other} onChange={(e) => setFieldSettings((prev) => ({ ...prev, remarks_required_for_other: e.target.checked }))} />
                        Remarks Mandatory for Other
                      </label>
                    </div>
                    <button type="button" onClick={saveFieldSettings} className="h-9 rounded-lg bg-slate-900 px-4 text-[11px] font-extrabold text-white hover:bg-slate-800">
                      Save Rules
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── MAIN FORM ── */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.32 }}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
            >
              <div className="h-[2px] bg-gradient-to-r from-indigo-500 to-emerald-500" />
              <div className="px-4 pt-4 pb-5 sm:px-6 sm:pt-5">

                {/* Form header */}
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-[9px] font-extrabold text-slate-400 uppercase tracking-[0.18em] mb-0.5">Form</p>
                    <h2 className="text-[13px] font-black text-slate-700">Attribute Details</h2>
                  </div>
                  <span className="text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg">
                    {inputSets.length} {inputSets.length === 1 ? 'entry' : 'entries'}
                  </span>
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Attribute rows */}
                  <div className="space-y-4 mb-4">
                    <AnimatePresence>
                      {inputSets.map((inputSet, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, height: 0, y: -8 }}
                          animate={{ opacity: 1, height: 'auto', y: 0 }}
                          exit={{ opacity: 0, height: 0, y: -4 }}
                          transition={{ duration: 0.22 }}
                          className="overflow-hidden"
                        >
                          <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 hover:border-indigo-200 transition-colors">
                            <div className="flex items-start gap-3">
                              {/* Serial Number */}
                              <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-[11px] font-extrabold text-indigo-600">{String(index + 1).padStart(2, '0')}</span>
                              </div>

                              {/* Fields - Grid Layout */}
                              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                {/* Created By */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Created By
                                  </label>
                                  <input
                                    type="text"
                                    value={inputSet.created_by}
                                    onChange={(e) => handleInputChange(index, 'created_by', e.target.value)}
                                    className="w-full h-9 px-3 text-[12px] font-medium text-slate-700 placeholder-slate-300 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                    placeholder="Person Name"
                                    required
                                  />
                                </div>

                                {/* Created Date */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Created Date
                                  </label>
                                  <div className="relative">
                                    <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                    <input
                                      type="date"
                                      value={inputSet.created_date}
                                      onChange={(e) => handleInputChange(index, 'created_date', e.target.value)}
                                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                      required
                                    />
                                  </div>
                                </div>

                                {/* Main Head Name */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Main Head <span className="text-rose-400">*</span>
                                  </label>
                                  <div className="relative">
                                    <TagIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                    <input
                                      list={`main-head-options-${index}`}
                                      type="text"
                                      value={inputSet.main_head_name}
                                      onChange={(e) => handleInputChange(index, 'main_head_name', e.target.value)}
                                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                      placeholder="Main Head"
                                      required
                                    />
                                    <datalist id={`main-head-options-${index}`}>
                                      {mainHeadOptions.map((head) => (
                                        <option key={head} value={head} />
                                      ))}
                                    </datalist>
                                  </div>
                                  {mainHeadOptions.length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setFetchPickers((prev) => ({ ...prev, [index]: { ...(prev[index] || {}), main: !prev[index]?.main } }))}
                                      className="mt-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-800"
                                    >
                                      Fetch Main Head
                                    </button>
                                  )}
                                  {fetchPickers[index]?.main && (
                                    <select
                                      value={inputSet.main_head_name}
                                      onChange={(e) => handleInputChange(index, 'main_head_name', e.target.value)}
                                      className="mt-1 w-full h-8 px-2 text-[11px] font-semibold text-slate-700 bg-white border border-indigo-100 rounded-lg outline-none focus:border-indigo-400"
                                    >
                                      <option value="">Select existing main head</option>
                                      {mainHeadOptions.map((head) => (
                                        <option key={head} value={head}>{head}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* Sub Main Name */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Sub Head
                                  </label>
                                  <div className="relative">
                                    <DocumentTextIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                    <input
                                      type="text"
                                      value={inputSet.sub_main_name}
                                      onChange={(e) => handleInputChange(index, 'sub_main_name', e.target.value)}
                                      className={`w-full h-9 pl-8 pr-3 text-[12px] font-medium rounded-lg outline-none transition-all ${
                                        inputSet.main_head_name
                                          ? 'text-slate-700 bg-white border border-slate-200 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100'
                                          : 'text-slate-400 bg-slate-100 border border-slate-200 cursor-not-allowed'
                                      }`}
                                      placeholder="Sub Head"
                                      disabled={!inputSet.main_head_name}
                                      required
                                    />
                                  </div>
                                  {inputSet.main_head_name && subHeadOptionsFor(inputSet.main_head_name).length > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setFetchPickers((prev) => ({ ...prev, [index]: { ...(prev[index] || {}), sub: !prev[index]?.sub } }))}
                                      className="mt-1 text-[10px] font-bold text-indigo-600 hover:text-indigo-800"
                                    >
                                      Fetch Sub Head
                                    </button>
                                  )}
                                  {fetchPickers[index]?.sub && inputSet.main_head_name && (
                                    <select
                                      value={inputSet.sub_main_name}
                                      onChange={(e) => handleInputChange(index, 'sub_main_name', e.target.value)}
                                      className="mt-1 w-full h-8 px-2 text-[11px] font-semibold text-slate-700 bg-white border border-indigo-100 rounded-lg outline-none focus:border-indigo-400"
                                    >
                                      <option value="">Select existing sub head</option>
                                      {subHeadOptionsFor(inputSet.main_head_name).map((subHead) => (
                                        <option key={subHead} value={subHead}>{subHead}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>

                                {/* Start Date */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    Start Date <span className="text-rose-400">*</span>
                                  </label>
                                  <div className="relative">
                                    <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                    <input
                                      type="date"
                                      value={inputSet.start_date}
                                      onChange={(e) => handleInputChange(index, 'start_date', e.target.value)}
                                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                      required
                                    />
                                  </div>
                                </div>

                                {/* End Date */}
                                <div>
                                  <label className="text-[9px] font-extrabold text-slate-500 uppercase tracking-[0.18em] block mb-1.5">
                                    End Date <span className="text-rose-400">*</span>
                                  </label>
                                  <div className="relative">
                                    <CalendarIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                                    <input
                                      type="date"
                                      value={inputSet.end_date}
                                      onChange={(e) => handleInputChange(index, 'end_date', e.target.value)}
                                      className="w-full h-9 pl-8 pr-3 text-[12px] font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Remove button */}
                              {inputSets.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => handleRemoveInputSet(index)}
                                  className="mt-5 w-8 h-8 rounded-lg bg-rose-50 border border-rose-200 text-rose-500 flex items-center justify-center hover:bg-rose-100 hover:border-rose-300 transition-all flex-shrink-0 touch-manipulation"
                                  aria-label="Remove attribute"
                                >
                                  <TrashIcon className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center gap-3 flex-wrap pt-2">
                    <button
                      type="button"
                      onClick={handleAddInputSet}
                      className="flex items-center gap-2 h-9 px-4 bg-white border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 text-[11px] font-bold rounded-lg transition-all touch-manipulation"
                    >
                      <PlusIcon className="w-3.5 h-3.5" />
                      Add Another
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="flex items-center gap-2 h-9 px-5 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white text-[11px] font-extrabold rounded-lg transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ml-auto"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                          Saving…
                        </>
                      ) : (
                        <>
                          <CheckCircleIcon className="w-3.5 h-3.5" />
                          Save {inputSets.length > 1 ? `${inputSets.length} Attributes` : 'Attribute'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>

            {/* Mobile bottom spacer */}
            <div className="h-8 sm:hidden" />
          </div>
        </main>
      </div>
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
    </div>
  );
};

export default Addattribute;
